import React, { useState, useEffect } from 'react';
import Header from '../../components/Header';
import { styles, colors, radius, shadows } from '../../styles/theme';
import { ROLE_DEFINITIONS, PERMISSIONS, hasPermission } from '../../auth/permissions';
import { useAuth } from '../../auth/AuthProvider';
import { invalidateQueries } from '../../hooks/useCachedQuery';
import { useToast } from '../../components/Toast';
import { logError } from '../../utils/logError';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const ROLES = [
  { value: 'yonetim', label: 'Yonetim' },
  { value: 'satinalma', label: 'Satinalma' },
  { value: 'üretim', label: 'Üretim' },
  { value: 'depo', label: 'Depo' },
  { value: 'sube', label: 'Sube' },
  { value: 'muhasebe', label: 'Muhasebe' },
  { value: 'operasyon', label: 'Operasyon' },
  { value: 'ik', label: 'İnsan Kaynakları' },
];

const ROLE_COLORS = {
  yonetim: '#1565c0', satinalma: '#2e7d32', üretim: '#e65100',
  depo: '#6a1b9a', sube: '#00838f', muhasebe: '#c62828', operasyon: '#37474f',
  ik: '#ad1457',
};

// Yetki kategorileri (okunabilir)
const PERMISSION_GROUPS = [
  { label: 'Pano', perms: [PERMISSIONS.VIEW_PAGE_DASHBOARD] },
  { label: 'Talep/Sipariş', perms: [PERMISSIONS.VIEW_PAGE_REQUESTS, PERMISSIONS.VIEW_PAGE_ORDERS, PERMISSIONS.VIEW_PAGE_APPROVALS, PERMISSIONS.CREATE_REQUEST, PERMISSIONS.CREATE_ORDER, PERMISSIONS.VIEW_ALL_REQUESTS, PERMISSIONS.APPROVE_REQUEST, PERMISSIONS.REJECT_REQUEST, PERMISSIONS.CONVERT_TO_ORDER, PERMISSIONS.VIEW_ALL_ORDERS, PERMISSIONS.APPROVE_ORDER_L1, PERMISSIONS.APPROVE_ORDER_L2] },
  { label: 'Stok/Depo', perms: [PERMISSIONS.VIEW_PAGE_GRN, PERMISSIONS.VIEW_PAGE_INVENTORY, PERMISSIONS.VIEW_STOCK, PERMISSIONS.MANAGE_GRN, PERMISSIONS.MANAGE_INVENTORY_TRANSFER] },
  { label: 'Üretim', perms: [PERMISSIONS.VIEW_PAGE_PRODUCTION, PERMISSIONS.MANAGE_PRODUCTION] },
  { label: 'Reçete/Maliyet', perms: [PERMISSIONS.VIEW_PAGE_RECIPES, PERMISSIONS.MANAGE_RECIPES, PERMISSIONS.VIEW_RECIPE_COSTS, PERMISSIONS.MANAGE_TRANSFER_PRICES, PERMISSIONS.ACCESS_SECRET_RECIPES] },
  { label: 'Finans', perms: [PERMISSIONS.VIEW_PAGE_FINANCE] },
  { label: 'Raporlar', perms: [PERMISSIONS.VIEW_PAGE_REPORTS, PERMISSIONS.VIEW_COST_REPORTS, PERMISSIONS.VIEW_SALES_REPORTS, PERMISSIONS.VIEW_PERFORMANCE_REPORTS] },
  { label: 'Tedarikçiler', perms: [PERMISSIONS.VIEW_PAGE_SUPPLIERS, PERMISSIONS.MANAGE_SUPPLIERS] },
  { label: 'POS', perms: [PERMISSIONS.VIEW_POS_DATA, PERMISSIONS.MANAGE_POS_DATA, PERMISSIONS.MANAGE_POS_MAPPINGS] },
  { label: 'Ödeme Planı', perms: [PERMISSIONS.CREATE_PAYMENT_PLAN, PERMISSIONS.MANAGE_PAYMENT_PLAN, PERMISSIONS.VIEW_PAYMENT_PLAN] },
  { label: 'Admin', perms: [PERMISSIONS.MANAGE_USERS, PERMISSIONS.MANAGE_SETTINGS] },
];

// Okunabilir permission isimleri
const PERMISSION_LABELS = {
  [PERMISSIONS.VIEW_PAGE_DASHBOARD]: 'Pano Sayfası',
  [PERMISSIONS.VIEW_PAGE_REQUESTS]: 'Talepler Sayfası',
  [PERMISSIONS.VIEW_PAGE_ORDERS]: 'Siparişler Sayfası',
  [PERMISSIONS.VIEW_PAGE_APPROVALS]: 'Onaylar Sayfası',
  [PERMISSIONS.VIEW_PAGE_GRN]: 'Mal Kabul Sayfası',
  [PERMISSIONS.VIEW_PAGE_PRODUCTION]: 'Üretim Sayfası',
  [PERMISSIONS.VIEW_PAGE_FINANCE]: 'Finans Sayfası',
  [PERMISSIONS.VIEW_PAGE_REPORTS]: 'Raporlar Sayfası',
  [PERMISSIONS.VIEW_PAGE_ADMIN]: 'Admin Sayfası',
  [PERMISSIONS.VIEW_PAGE_SUPPLIERS]: 'Tedarikçiler Sayfası',
  [PERMISSIONS.MANAGE_SUPPLIERS]: 'Tedarikçi Ekle/Güncelle',
  [PERMISSIONS.VIEW_PAGE_INVENTORY]: 'Envanter Sayfası',
  [PERMISSIONS.VIEW_ALL_REQUESTS]: 'Tüm Talepleri Gör',
  [PERMISSIONS.CREATE_REQUEST]: 'Talep Oluştur',
  [PERMISSIONS.APPROVE_REQUEST]: 'Talep Onayla',
  [PERMISSIONS.REJECT_REQUEST]: 'Talep Reddet',
  [PERMISSIONS.CONVERT_TO_ORDER]: 'Siparişe Dönüştür',
  [PERMISSIONS.CREATE_ORDER]: 'Sipariş Oluştur',
  [PERMISSIONS.VIEW_ALL_ORDERS]: 'Tüm Siparişleri Gör',
  [PERMISSIONS.APPROVE_ORDER_L1]: 'Sipariş Onay L1',
  [PERMISSIONS.APPROVE_ORDER_L2]: 'Sipariş Onay L2',
  [PERMISSIONS.MANAGE_GRN]: 'Mal Kabul Yönet',
  [PERMISSIONS.MANAGE_PRODUCTION]: 'Üretim Yönet',
  [PERMISSIONS.VIEW_STOCK]: 'Stok Görüntüle',
  [PERMISSIONS.MANAGE_INVENTORY_TRANSFER]: 'Transfer Yönet',
  [PERMISSIONS.VIEW_COST_REPORTS]: 'Maliyet Raporları',
  [PERMISSIONS.VIEW_SALES_REPORTS]: 'Satış Raporları',
  [PERMISSIONS.VIEW_PERFORMANCE_REPORTS]: 'Performans Raporları',
  [PERMISSIONS.VIEW_PAGE_RECIPES]: 'Reçete Sayfası',
  [PERMISSIONS.MANAGE_RECIPES]: 'Reçete Yönet',
  [PERMISSIONS.VIEW_RECIPE_COSTS]: 'Reçete Maliyetleri',
  [PERMISSIONS.MANAGE_TRANSFER_PRICES]: 'Transfer Fiyatları',
  [PERMISSIONS.ACCESS_SECRET_RECIPES]: '🔒 Gizli Reçete İçeriği',
  [PERMISSIONS.VIEW_POS_DATA]: 'POS Verileri Gör',
  [PERMISSIONS.MANAGE_POS_DATA]: 'POS Verileri Yönet',
  [PERMISSIONS.MANAGE_POS_MAPPINGS]: 'POS Eşleştirme Yönet',
  [PERMISSIONS.CREATE_PAYMENT_PLAN]: 'Ödeme Planı Oluştur',
  [PERMISSIONS.MANAGE_PAYMENT_PLAN]: 'Ödeme Planı Yönet',
  [PERMISSIONS.VIEW_PAYMENT_PLAN]: 'Ödeme Planı Görüntüle',
  [PERMISSIONS.MANAGE_USERS]: 'Kullanıcı Yönet',
  [PERMISSIONS.MANAGE_SETTINGS]: 'Ayarlar Yönet',
};

const KullanicilarPage = () => {
  const toast = useToast();
  const { user: currentUser } = useAuth();
  // Gizli reçete görme iznini (ACCESS_SECRET_RECIPES) yalnız GRANT_SECRET_RECIPES
  // sahibi (Hakan/admin) atayabilir. Diğerlerinde o kutu disabled. Güvenlik backend'de.
  const canGrantSecret = currentUser && hasPermission(
    currentUser.role, PERMISSIONS.GRANT_SECRET_RECIPES, currentUser.effectivePermissions
  );
  const [users, setUsers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState({ ad: '', email: '', sifre: '', rol: 'satinalma', departman: '', lokasyon_id: '' });
  const [showPerms, setShowPerms] = useState(false);
  const [customPerms, setCustomPerms] = useState([]);
  const [loadingPerms, setLoadingPerms] = useState(false);
  const [showCustomPerms, setShowCustomPerms] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('access_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  const fetchUsers = () => {
    fetch(`${API_URL}/api/users`, { headers: getAuthHeaders() })
      .then(res => { if (!res.ok) throw new Error('Auth hatası'); return res.json(); })
      .then(data => setUsers(Array.isArray(data) ? data : []))
      .catch(err => logError('Kullanicilar yüklenemedi:', err));
  };

  useEffect(() => { fetchUsers(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Güçlü geçici şifre üret (kripto-güvenli). Karıştırılabilir karakterler (0/O, 1/l/I) hariç.
  const genTempPassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%*';
    const arr = new Uint32Array(14);
    (window.crypto || window.msCrypto).getRandomValues(arr);
    return Array.from(arr, (n) => chars[n % chars.length]).join('');
  };

  const handleSave = async () => {
    const payload = { ...form };
    if (payload.lokasyon_id === '') payload.lokasyon_id = null;

    // Güvenlik: zayıf sabit default ('1234') yok. Şifre boş bırakılırsa güçlü geçici şifre üretilir.
    let tempPassword = null;
    if (!payload.sifre) {
      tempPassword = genTempPassword();
      payload.sifre = tempPassword;
    }

    const res = await fetch(`${API_URL}/api/users`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(payload)
    });
    if (res.ok) {
      invalidateQueries(['kullanici', 'admin']);
      setShowModal(false);
      setForm({ ad: '', email: '', sifre: '', rol: 'satinalma', departman: '', lokasyon_id: '' });
      fetchUsers();
      if (tempPassword) {
        window.alert(
          `Kullanıcı oluşturuldu.\n\nGeçici şifre: ${tempPassword}\n\n` +
          `Bu şifreyi kullanıcıya güvenli bir kanaldan iletin. İlk girişte değiştirmesi önerilir.`
        );
      }
    } else {
      toast.error('Hata! (Lütfen emailin benzersiz oldugundan emin olun)');
    }
  };

  const fetchCustomPerms = async (userId) => {
    setLoadingPerms(true);
    try {
      const res = await fetch(`${API_URL}/api/kullanici-izinleri/${userId}`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setCustomPerms(data.permissions || []);
      } else {
        setCustomPerms([]);
      }
    } catch {
      setCustomPerms([]);
    }
    setLoadingPerms(false);
  };

  const saveCustomPerms = async (userId, perms) => {
    try {
      await fetch(`${API_URL}/api/kullanici-izinleri/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ permissions: perms })
      });
    } catch (err) {
      logError('Izin kaydetme hatası:', err);
    }
  };

  const handleEdit = (u) => {
    setEditingUser(u);
    setForm({ ad: u.ad, email: u.email, sifre: '', rol: u.rol, departman: u.departman || '', lokasyon_id: u.lokasyon_id || '' });
    setShowCustomPerms(false);
    setCustomPerms([]);
    fetchCustomPerms(u.id);
    setShowModal(true);
  };

  const handleUpdate = async () => {
    const payload = {};
    if (form.ad) payload.ad = form.ad;
    if (form.email) payload.email = form.email;
    if (form.sifre) payload.sifre = form.sifre;
    if (form.rol) payload.rol = form.rol;
    if (form.departman) payload.departman = form.departman;

    const res = await fetch(`${API_URL}/api/users/${editingUser.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(payload)
    });
    if (res.ok) {
      // Custom permission'lari da kaydet
      await saveCustomPerms(editingUser.id, customPerms);
      invalidateQueries(['kullanici', 'admin']);
      setShowModal(false);
      setEditingUser(null);
      setCustomPerms([]);
      setShowCustomPerms(false);
      setForm({ ad: '', email: '', sifre: '', rol: 'satinalma', departman: '', lokasyon_id: '' });
      fetchUsers();
    } else {
      const err = await res.json().catch(() => ({}));
      toast.error(err.detail || 'Guncelleme hatası');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Silmek istediğinize emin misiniz?')) return;
    await fetch(`${API_URL}/api/users/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    fetchUsers();
  };

  const openNewModal = () => {
    setEditingUser(null);
    setForm({ ad: '', email: '', sifre: '', rol: 'satinalma', departman: '', lokasyon_id: '' });
    setShowModal(true);
  };

  return (
    <div>
      <Header title="Kullanıcı Yonetimi" />
      <div style={{ padding: '20px', background: colors.background, minHeight: 'calc(100vh - 60px)' }}>

        <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
          <button onClick={openNewModal} style={{ padding: '10px 20px', background: styles.success, color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold' }}>
            + Yeni Kullanıcı Ekle
          </button>
          <button onClick={() => setShowPerms(!showPerms)} style={{ padding: '10px 20px', background: showPerms ? styles.primary : 'white', color: showPerms ? 'white' : '#333', border: `1px solid ${colors.border}`, borderRadius: '5px', cursor: 'pointer' }}>
            {showPerms ? 'Yetki Tablosunu Gizle' : 'Rol Yetkileri'}
          </button>
        </div>

        {/* Yetki Tablosu */}
        {showPerms && (
          <div style={{ background: colors.surface, padding: '20px', borderRadius: radius.md, marginBottom: '20px', overflowX: 'auto' }}>
            <h4 style={{ marginTop: 0 }}>Rol - Yetki Matrisi</h4>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr>
                  <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Modul</th>
                  {ROLES.map(r => (
                    <th key={r.value} style={{ padding: '8px', textAlign: 'center', borderBottom: '2px solid #ddd', color: ROLE_COLORS[r.value] || '#333' }}>
                      {r.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {PERMISSION_GROUPS.map(group => {
                  return (
                    <tr key={group.label}>
                      <td style={{ padding: '8px', fontWeight: 600, borderBottom: `1px solid ${colors.border}` }}>{group.label}</td>
                      {ROLES.map(r => {
                        const rolePerms = ROLE_DEFINITIONS[r.value] || [];
                        const hasAny = group.perms.some(p => rolePerms.includes(p));
                        const hasAll = group.perms.every(p => rolePerms.includes(p));
                        return (
                          <td key={r.value} style={{ padding: '8px', textAlign: 'center', borderBottom: `1px solid ${colors.border}` }}>
                            {hasAll ? (
                              <span style={{ color: '#2e7d32', fontWeight: 700, fontSize: '16px' }}>&#10003;</span>
                            ) : hasAny ? (
                              <span style={{ color: '#f9a825', fontWeight: 700, fontSize: '14px' }}>~</span>
                            ) : (
                              <span style={{ color: '#ccc' }}>-</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#888' }}>
              &#10003; = Tam erisim &nbsp; ~ = Kismi erisim &nbsp; - = Erisim yok
            </div>
          </div>
        )}

        {/* Kullanıcı Kartlari */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
          {users.map(u => (
            <div key={u.id} style={{ background: colors.surface, padding: '20px', borderRadius: radius.md, boxShadow: shadows.sm, position: 'relative', borderLeft: `4px solid ${ROLE_COLORS[u.rol] || '#ccc'}` }}>
              <div style={{ position: 'absolute', top: '15px', right: '15px' }}>
                <span style={{ background: ROLE_COLORS[u.rol] || '#eee', color: 'white', padding: '3px 10px', borderRadius: radius.lg, fontSize: '11px', fontWeight: 600 }}>
                  {u.rol}
                </span>
              </div>
              <h3 style={{ margin: '0 0 8px 0', color: '#333' }}>{u.ad}</h3>
              <p style={{ margin: '4px 0', color: colors.textSecondary, fontSize: '14px' }}>{u.email}</p>
              {u.departman && <p style={{ margin: '4px 0', color: colors.textTertiary, fontSize: '13px' }}>{u.departman}</p>}

              <div style={{ marginTop: '15px', borderTop: `1px solid ${colors.border}`, paddingTop: '12px', display: 'flex', gap: '8px' }}>
                <button onClick={() => handleEdit(u)} style={{ flex: 1, padding: '7px', background: styles.primary, color: 'white', border: 'none', borderRadius: radius.sm, cursor: 'pointer', fontSize: '13px' }}>
                  Düzenle
                </button>
                <button onClick={() => handleDelete(u.id)} style={{ padding: '7px 14px', background: '#f8d7da', color: '#721c24', border: 'none', borderRadius: radius.sm, cursor: 'pointer', fontSize: '13px' }}>
                  Sil
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Modal - Yeni / Düzenle */}
        {showModal && (
          <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => { setShowModal(false); setEditingUser(null); setCustomPerms([]); setShowCustomPerms(false); }}>
            <div style={{ background: colors.surface, padding: '30px', borderRadius: radius.md, width: editingUser ? '620px' : '420px', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
              <h3 style={{ marginTop: 0 }}>{editingUser ? `Düzenle: ${editingUser.ad}` : 'Yeni Kullanıcı'}</h3>

              <label style={{ fontSize: '12px', color: colors.textSecondary }}>Ad Soyad</label>
              <input value={form.ad} onChange={e => setForm({ ...form, ad: e.target.value })} style={{ display: 'block', width: '100%', padding: '10px', marginBottom: '12px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, boxSizing: 'border-box' }} />

              <label style={{ fontSize: '12px', color: colors.textSecondary }}>Email</label>
              <input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} style={{ display: 'block', width: '100%', padding: '10px', marginBottom: '12px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, boxSizing: 'border-box' }} />

              <label style={{ fontSize: '12px', color: colors.textSecondary }}>{editingUser ? 'Yeni Sifre (bos birakirsaniz degismez)' : 'Sifre'}</label>
              <input type="password" value={form.sifre} onChange={e => setForm({ ...form, sifre: e.target.value })} placeholder={editingUser ? 'Degistirmek icin yazin' : 'Bos birakirsaniz guclu sifre uretilir'} style={{ display: 'block', width: '100%', padding: '10px', marginBottom: '12px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, boxSizing: 'border-box' }} />

              <label style={{ fontSize: '12px', color: colors.textSecondary }}>Rol</label>
              <select value={form.rol} onChange={e => setForm({ ...form, rol: e.target.value })} style={{ display: 'block', width: '100%', padding: '10px', marginBottom: '12px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, boxSizing: 'border-box' }}>
                {ROLES.map(r => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>

              {/* Secilen rolun yetkileri */}
              <div style={{ background: colors.background, padding: '10px', borderRadius: radius.sm, marginBottom: '12px', fontSize: '12px' }}>
                <strong>Bu rolun yetkileri:</strong>
                <div style={{ marginTop: '6px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                  {PERMISSION_GROUPS.map(g => {
                    const rolePerms = ROLE_DEFINITIONS[form.rol] || [];
                    const has = g.perms.some(p => rolePerms.includes(p));
                    return has ? (
                      <span key={g.label} style={{ background: '#e8f5e9', color: '#2e7d32', padding: '2px 8px', borderRadius: radius.lg, fontSize: '11px' }}>
                        {g.label}
                      </span>
                    ) : null;
                  })}
                </div>
              </div>

              <label style={{ fontSize: '12px', color: colors.textSecondary }}>Departman</label>
              <input value={form.departman} onChange={e => setForm({ ...form, departman: e.target.value })} style={{ display: 'block', width: '100%', padding: '10px', marginBottom: '16px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, boxSizing: 'border-box' }} />

              {/* Ozel Izinler - sadece duzenleme modunda */}
              {editingUser && (
                <div style={{ marginBottom: '16px' }}>
                  <button
                    type="button"
                    onClick={() => setShowCustomPerms(!showCustomPerms)}
                    style={{ width: '100%', padding: '10px', background: showCustomPerms ? '#e3f2fd' : '#f5f5f5', color: showCustomPerms ? '#1565c0' : '#555', border: `1px solid ${colors.border}`, borderRadius: radius.sm, cursor: 'pointer', fontWeight: 600, fontSize: '13px', textAlign: 'left' }}
                  >
                    {showCustomPerms ? '▼' : '▶'} Ozel Izinler {customPerms.length > 0 && <span style={{ background: '#1565c0', color: 'white', padding: '1px 8px', borderRadius: radius.lg, fontSize: '11px', marginLeft: '8px' }}>{customPerms.length}</span>}
                  </button>
                  {showCustomPerms && (
                    <div style={{ border: `1px solid ${colors.border}`, borderTop: 'none', borderRadius: '0 0 4px 4px', padding: '12px', background: '#fafafa' }}>
                      {loadingPerms ? (
                        <p style={{ color: colors.textTertiary, fontSize: '13px', margin: 0 }}>Izinler yükleniyor...</p>
                      ) : (
                        <>
                          <p style={{ margin: '0 0 8px 0', fontSize: '12px', color: '#888' }}>
                            Rolun verdigi izinler gri, ekstra izinler secili gorunur. Sadece ekstra izinleri degistirebilirsiniz.
                          </p>
                          {PERMISSION_GROUPS.map(group => {
                            const rolePerms = ROLE_DEFINITIONS[form.rol] || [];
                            const groupHasRelevant = group.perms.some(p => !rolePerms.includes(p));
                            return (
                              <div key={group.label} style={{ marginBottom: '10px' }}>
                                <div style={{ fontWeight: 600, fontSize: '12px', color: '#333', marginBottom: '4px', borderBottom: `1px solid ${colors.border}`, paddingBottom: '2px' }}>
                                  {group.label}
                                </div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 12px' }}>
                                  {group.perms.map(perm => {
                                    const fromRole = rolePerms.includes(perm);
                                    const isCustom = customPerms.includes(perm);
                                    // Gizli içerik iznini yalnız GRANT_SECRET_RECIPES sahibi (Hakan) değiştirebilir
                                    const lockedSecret = perm === PERMISSIONS.ACCESS_SECRET_RECIPES && !canGrantSecret;
                                    return (
                                      <label key={perm} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: fromRole ? '#999' : '#333', cursor: fromRole ? 'default' : 'pointer', padding: '2px 0', minWidth: '180px' }}>
                                        <input
                                          type="checkbox"
                                          checked={fromRole || isCustom}
                                          disabled={fromRole || lockedSecret}
                                          onChange={(e) => {
                                            if (fromRole || lockedSecret) return;
                                            if (e.target.checked) {
                                              setCustomPerms(prev => [...prev, perm]);
                                            } else {
                                              setCustomPerms(prev => prev.filter(p => p !== perm));
                                            }
                                          }}
                                          style={{ margin: 0 }}
                                        />
                                        <span style={{ textDecoration: fromRole ? 'none' : 'none', fontStyle: fromRole ? 'italic' : 'normal' }}>
                                          {PERMISSION_LABELS[perm] || perm}
                                          {fromRole && <span style={{ fontSize: '10px', color: '#bbb' }}> (rol)</span>}
                                          {lockedSecret && <span style={{ fontSize: '10px', color: '#bbb' }}> (yalnız Hakan)</span>}
                                        </span>
                                      </label>
                                    );
                                  })}
                                </div>
                                {!groupHasRelevant && (
                                  <span style={{ fontSize: '11px', color: '#bbb', fontStyle: 'italic' }}>Tüm izinler rol tarafindan saglanmaktadir</span>
                                )}
                              </div>
                            );
                          })}
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div style={{ display: 'flex', gap: '10px' }}>
                <button onClick={editingUser ? handleUpdate : handleSave} style={{ flex: 1, padding: '10px', background: styles.success, color: 'white', border: 'none', borderRadius: radius.sm, cursor: 'pointer', fontWeight: 600 }}>
                  {editingUser ? 'Güncelle' : 'Kaydet'}
                </button>
                <button onClick={() => { setShowModal(false); setEditingUser(null); setCustomPerms([]); setShowCustomPerms(false); }} style={{ flex: 1, padding: '10px', background: '#666', color: 'white', border: 'none', borderRadius: radius.sm, cursor: 'pointer' }}>
                  İptal
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default KullanicilarPage;
